import { useCallback, useRef } from 'react';
import { useFlowView, useNodeData } from '@/components/Recipe/FlowContext';
import { pngDataUrlToBlob } from '@/components/Recipe/NodeAssetDownloader/markRenderedPicture';
import { useMarkedNodeRender } from '@/components/Recipe/NodeAssetDownloader/useMarkedNodeRender';
import { useInteractiveDownloader } from '@/hooks/useInteractiveDownloader';
import { log } from '@/logger/logger';
import { useWorkflowStore } from '@/state/workflow.state';
import type { BaseNodeData } from '@/types/node';
import type { NodeId, OutputId } from 'web';

const logger = log.getLogger('useNodeImageActions');

/**
 * Downloads or copies what a node displays, snapshotted to a PNG rather than run through a full
 * export. Shared by the viewers of graphics nodes; pass an output to pick one of several.
 */
export function useNodeImageActions(id: NodeId) {
  const flowView = useFlowView();
  const nodeName = useNodeData<BaseNodeData>(id).name;
  const workflowName = useWorkflowStore((state) => state.recipe?.name);
  const { downloadAssetFromNode } = useInteractiveDownloader();
  const renderMarkedImage = useMarkedNodeRender(id);
  /** Signing can take a while, so a repeat click must not upload and download the picture again. */
  const isDownloadingRef = useRef(false);

  const downloadNodeImage = useCallback(
    async (outputId?: OutputId): Promise<void> => {
      if (isDownloadingRef.current) return;
      isDownloadingRef.current = true;
      try {
        const picture = await renderMarkedImage({ outputId });
        if (!picture) {
          logger.error('Failed to render node image for download', { nodeId: id });
          return;
        }
        await downloadAssetFromNode({
          ...picture,
          fileType: 'image',
          nodeName: nodeName || id,
          prefix: workflowName,
        });
      } finally {
        isDownloadingRef.current = false;
      }
    },
    [renderMarkedImage, id, nodeName, workflowName, downloadAssetFromNode],
  );

  const copyNodeImage = useCallback(
    (outputId?: OutputId): void => {
      const dataUrl = flowView.renderNodeImage(id, outputId);
      if (!dataUrl) {
        logger.error('Failed to render node image for copy', { nodeId: id });
        return;
      }

      const clipboardItem = new ClipboardItem({ 'image/png': pngDataUrlToBlob(dataUrl) });
      void navigator.clipboard.write([clipboardItem]).catch(() => {
        logger.error('Failed to copy node image to clipboard', { nodeId: id });
      });
    },
    [flowView, id],
  );

  return { downloadNodeImage, copyNodeImage };
}
